import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg, specDisplay } from "@/lib/api";
import { useCategories } from "@/hooks/useMeta";
import SpecInput from "@/components/common/SpecInput";
import { Loading } from "@/components/common/States";

const EMPTY = { category: "fc", brand: "", model: "", revision: "", description: "", specs: {}, images: [], extra_info: "" };

function ProductDialog({ open, onClose, initial, onSaved }) {
  const { data: cats = [] } = useCategories();
  const [f, setF] = useState(initial);
  const cat = cats.find((c) => c.slug === f.category);
  const save = async () => {
    const body = { ...f, revision: f.revision || null, images: typeof f.images === "string" ? f.images.split(",").map((s) => s.trim()).filter(Boolean) : f.images };
    try {
      f.id ? await api.put(`/admin/products/${f.id}`, body) : await api.post("/admin/products", body);
      toast.success("Ürün kaydedildi"); onSaved(); onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader><DialogTitle>{f.id ? "Ürünü düzenle" : "Yeni katalog ürünü"}</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v, specs: {} })} disabled={!!f.id}>
            <SelectTrigger data-testid="admin-product-category"><SelectValue /></SelectTrigger>
            <SelectContent>{cats.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
          <Input data-testid="admin-product-brand" placeholder="Marka" value={f.brand} onChange={(e) => setF({ ...f, brand: e.target.value })} />
          <Input data-testid="admin-product-model" placeholder="Model" value={f.model} onChange={(e) => setF({ ...f, model: e.target.value })} />
          <Input data-testid="admin-product-revision" placeholder="Donanım revizyonu (ops.)" value={f.revision || ""} onChange={(e) => setF({ ...f, revision: e.target.value })} />
        </div>
        <Textarea placeholder="Açıklama" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
        <Input placeholder="Görsel URL'leri (virgülle)" value={Array.isArray(f.images) ? f.images.join(", ") : f.images} onChange={(e) => setF({ ...f, images: e.target.value })} />
        <div className="space-y-2">
          {cat?.fields.map((fld) => (
            <div key={fld.key} className="grid items-center gap-2 sm:grid-cols-[180px_1fr]">
              <Label className="text-xs text-zinc-500">{fld.label}</Label>
              <SpecInput prefix="admin-spec" field={fld} value={f.specs[fld.key]} onChange={(v) => setF({ ...f, specs: { ...f.specs, [fld.key]: v } })} />
            </div>
          ))}
        </div>
        <Button onClick={save} data-testid="admin-product-save" className="bg-[#0071e3] text-white">Kaydet</Button>
      </DialogContent>
    </Dialog>
  );
}

export function AdminProducts() {
  const qc = useQueryClient();
  const { data: cats = [] } = useCategories();
  const [cat, setCat] = useState("fc");
  const [edit, setEdit] = useState(null);
  const { data, isLoading } = useQuery({ queryKey: ["products", "admin", cat], queryFn: async () => (await api.get(`/products?category=${cat}&limit=100`)).data });
  const refresh = () => qc.invalidateQueries({ queryKey: ["products"] });
  const addRevision = async (p) => {
    const rev = window.prompt(`${p.brand} ${p.model} için yeni revizyon adı (örn. V2):`);
    if (!rev) return;
    try { await api.post(`/admin/products/${p.id}/revisions`, { revision: rev, specs: {} }); toast.success("Revizyon eklendi"); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  const archive = async (p) => { if (!window.confirm("Ürün arşivlensin mi?")) return; await api.delete(`/admin/products/${p.id}`); refresh(); };
  return (
    <div className="space-y-4" data-testid="admin-products">
      <div className="flex flex-wrap gap-2">
        <Select value={cat} onValueChange={setCat}><SelectTrigger className="w-60" data-testid="admin-products-category"><SelectValue /></SelectTrigger>
          <SelectContent>{cats.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent></Select>
        <Button onClick={() => setEdit({ ...EMPTY, category: cat })} data-testid="admin-new-product" className="bg-[#0071e3] text-white">Yeni ürün</Button>
      </div>
      {isLoading ? <Loading /> : (
        <div className="panel divide-y divide-black/[0.06]">
          {data.items.map((p) => (
            <div key={p.id} className="flex flex-wrap items-center justify-between gap-2 p-3 text-sm" data-testid={`admin-product-${p.id}`}>
              <span>{p.title} {p.is_test_data && <span className="text-[10px] text-zinc-500">test verisi</span>}</span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="bg-transparent" onClick={() => setEdit({ ...p, revision: p.revision || "" })} data-testid={`admin-edit-product-${p.id}`}>Düzenle</Button>
                <Button size="sm" variant="outline" className="bg-transparent" onClick={() => addRevision(p)}>+ Revizyon</Button>
                <Button size="sm" variant="outline" className="bg-transparent text-red-700" onClick={() => archive(p)}>Arşivle</Button>
              </div>
            </div>
          ))}
        </div>
      )}
      {edit && <ProductDialog open initial={edit} onClose={() => setEdit(null)} onSaved={refresh} />}
    </div>
  );
}

export function AdminCategories() {
  const qc = useQueryClient();
  const { data: cats = [] } = useCategories();
  const [slug, setSlug] = useState("vtx");
  const [draft, setDraft] = useState(null);
  const cat = draft || cats.find((c) => c.slug === slug);
  if (!cat) return <Loading />;
  const upd = (i, patch) => setDraft({ ...cat, fields: cat.fields.map((f, n) => (n === i ? { ...f, ...patch } : f)) });
  const save = async () => {
    try {
      const fields = cat.fields.map((f) => ({ ...f, options: typeof f.options === "string" ? f.options.split(",").map((s) => s.trim()).filter(Boolean) : f.options || null }));
      await api.put(`/admin/categories/${cat.slug}`, { ...cat, fields });
      toast.success("Kategori kaydedildi"); setDraft(null); qc.invalidateQueries({ queryKey: ["categories"] });
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-4" data-testid="admin-categories">
      <Select value={slug} onValueChange={(v) => { setSlug(v); setDraft(null); }}><SelectTrigger className="w-60" data-testid="admin-category-select"><SelectValue /></SelectTrigger>
        <SelectContent>{cats.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent></Select>
      <Input data-testid="admin-category-name" value={cat.name} onChange={(e) => setDraft({ ...cat, name: e.target.value })} />
      <div className="overflow-x-auto panel">
        <table className="w-full text-xs"><thead className="text-zinc-500"><tr><th className="p-2 text-left">Anahtar</th><th className="p-2 text-left">Etiket</th><th className="p-2">Tip</th><th className="p-2">Birim</th><th className="p-2">Seçenekler</th><th className="p-2">Filtre</th></tr></thead>
          <tbody>{cat.fields.map((f, i) => (
            <tr key={i} className="border-t border-black/[0.08]">
              <td className="p-1"><Input className="h-8 font-mono text-xs" value={f.key} disabled={!f._new} onChange={(e) => upd(i, { key: e.target.value })} /></td>
              <td className="p-1"><Input className="h-8 text-xs" value={f.label} onChange={(e) => upd(i, { label: e.target.value })} /></td>
              <td className="p-1"><Select value={f.type} onValueChange={(v) => upd(i, { type: v })}><SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{["number", "text", "select", "multiselect", "boolean"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></td>
              <td className="p-1"><Input className="h-8 w-16 text-xs" value={f.unit || ""} onChange={(e) => upd(i, { unit: e.target.value || null })} /></td>
              <td className="p-1"><Input className="h-8 text-xs" value={Array.isArray(f.options) ? f.options.join(", ") : f.options || ""} onChange={(e) => upd(i, { options: e.target.value })} /></td>
              <td className="p-1 text-center"><Checkbox checked={f.filter} onCheckedChange={(c) => upd(i, { filter: !!c })} /></td>
            </tr>))}</tbody></table>
      </div>
      <div className="flex gap-2">
        <Button variant="outline" className="bg-transparent" data-testid="admin-add-field" onClick={() => setDraft({ ...cat, fields: [...cat.fields, { key: "", label: "", type: "text", filter: false, _new: true }] })}>+ Alan ekle</Button>
        <Button onClick={save} data-testid="admin-category-save" className="bg-[#0071e3] text-white">Kaydet</Button>
      </div>
    </div>
  );
}

export function AdminCorrections() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["admin-corrections"], queryFn: async () => (await api.get("/admin/corrections")).data });
  const decide = async (id, action) => {
    try { await api.post(`/admin/corrections/${id}`, { action }); toast.success(action === "approve" ? "Öneri kataloğa uygulandı" : "Öneri reddedildi"); qc.invalidateQueries({ queryKey: ["admin-corrections"] }); qc.invalidateQueries({ queryKey: ["admin-stats"] }); } catch (e) { toast.error(errMsg(e)); }
  };
  if (isLoading) return <Loading />;
  if (!data.length) return <p className="text-sm text-zinc-500" data-testid="no-corrections">Bekleyen düzeltme önerisi yok.</p>;
  return (
    <div className="space-y-2" data-testid="admin-corrections">
      {data.map((c) => (
        <div key={c.id} className="panel flex flex-wrap items-center justify-between gap-3 p-4 text-sm" data-testid={`correction-${c.id}`}>
          <div><p className="font-medium">{c.product_title} · <span className="font-mono">{c.field_key}</span></p>
            <p className="text-zinc-500">Mevcut: {specDisplay(null, c.current_value) ?? "Bilinmiyor"} → Önerilen: <span className="text-[#0077ed]">{c.proposed_value}</span></p>
            {c.note && <p className="text-xs text-zinc-500">{c.note} — {c.user_name}</p>}</div>
          <div className="flex gap-2"><Button size="sm" onClick={() => decide(c.id, "approve")} data-testid={`approve-correction-${c.id}`} className="bg-emerald-600">Onayla</Button>
            <Button size="sm" variant="outline" className="bg-transparent" onClick={() => decide(c.id, "reject")} data-testid={`reject-correction-${c.id}`}>Reddet</Button></div>
        </div>
      ))}
    </div>
  );
}
